import React, { useState, useRef } from "react";
import { Form, Alert, Button } from "react-bootstrap";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import ApiService from "../services/ApiService"; 

/**
 * ImageUpload Component
 *
 * Un composant d'upload d'image simplifié qui renvoie l'objet File directement.
 *
 * @param {Object} props
 * @param {string|null} props.value - L'URL d'aperçu (CHEMIN COMPLET ou URL Blob, géré par le parent)
 * @param {Function} props.onChange - Callback fonction qui reçoit l'objet File binaire ou null
 * @param {string} props.label - Label du champ d'upload
 * @param {number} props.maxSizeMB - Taille maximale du fichier en MB (default: 5)
 * @param {string[]} props.acceptedFormats - Formats d'image acceptés (default: ['image/png', 'image/jpeg', 'image/jpg'])
 * @param {boolean} props.disabled - Désactiver l'upload
 */
const ImageUpload = ({
  value = null, // Doit être l'URL complète (http://... ou blob:...)
  onChange,
  label = "Télécharger une image",
  maxSizeMB = 5,
  maxImageWidth = 150, 
  maxImageHeight = 75,
  acceptedFormats = ["image/png", "image/jpeg", "image/jpg"],
  required = false,
}) => {
  const [error, setError] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [imageInfo, setImageInfo] = useState(null);
  const fileInputRef = useRef(null);

  // NOTE: setBase64Preview est conservé ici s'il est utilisé pour le drag & drop,
  // mais la logique qui s'appuyait dessus pour l'affichage sera simplifiée.

  /**
   * Validates file type and size ONLY (pas de dimensions)
   * @param {File} file - The uploaded file
   */
  const handleFile = (file) => {
    setError("");

    // 1. Validation du type de fichier
    if (!acceptedFormats.includes(file.type)) {
      setError("Format non accepté. Seuls PNG et JPG (JPEG) sont autorisés.");
      return;
    }

    // 2. Validation de la taille du fichier
    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > maxSizeMB) {
      setError(
        `Le fichier est trop volumineux. Taille maximale : ${maxSizeMB}MB.`
      );
      return;
    }

    // Renvoie l'objet File binaire directement au parent pour le recadrage
    onChange(file);
    
    // Réinitialisation des informations d'image car elles seront incorrectes avant le recadrage
    setImageInfo(null);
  };

  /**
   * Handles file input change
   */
  const handleInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFile(file);
    }
  };

  /**
   * Handles drag over event
   */
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  /**
   * Handles drag leave event
   */
  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  /**
   * Handles drop event
   */
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFile(file);
    }
  };

  /**
   * Clears the uploaded image
   */
  const handleClear = () => {
    setError("");
    setImageInfo(null);
    onChange(null); // Renvoie null au parent pour suppression
    if (fileInputRef.current) {
      fileInputRef.current.value = ""; // Réinitialise l'input file
    }
  };

  /**
   * Opens file picker
   */
  const handleClick = () => {
    fileInputRef.current?.click();
  };

// --- LOGIQUE CRUCIALE D'AFFICHAGE : UTILISER 'value' DIRECTEMENT ---
// Le parent doit garantir que 'value' est une URL complète ou null.
const isImagePresent = !!value; 
const finalImageSrc = value; // Nous nous fions entièrement à la valeur passée.
// ---------------------------------------------

  return (
    <Form.Group className="my-3">
      <Form.Label>
        {label}
        {required && <span className="text-danger"> *</span>}
      </Form.Label>

      {!isImagePresent ? (
        <div
          className={`image-upload-zone ${isDragging ? "dragging" : ""}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleClick}
          style={{
            border: isDragging
              ? "2px solid #0d6efd"
              : "2px dashed #ced4da",
            borderRadius: "8px",
            padding: "2rem",
            textAlign: "center",
            cursor: "pointer",
            backgroundColor: isDragging ? "#f0f8ff" : "#f8f9fa",
            transition: "all 0.2s ease",
          }}
        >
          <CloudUploadOutlinedIcon
            style={{ fontSize: "3rem", color: "#6c757d", marginBottom: "1rem" }}
          />
          <p className="mb-2" style={{ color: "#6c757d" }}>
            <strong>Cliquez pour télécharger</strong> ou glissez-déposez
          </p>
          <p className="mb-0" style={{ fontSize: "0.875rem", color: "#6c757d" }}>
            PNG, JPG ou JPEG (Max. {maxSizeMB}MB)
          </p>
          <Form.Control
            type="file"
            ref={fileInputRef}
            onChange={handleInputChange}
            accept={acceptedFormats.join(",")}
            style={{ display: "none" }}
            required={required && !value}
          />
        </div>
      ) : (
        <div
          className="image-preview-container"
          style={{
            border: "1px solid #ced4da",
            borderRadius: "8px",
            padding: "1rem",
            position: "relative",
          }}
        >
          <div className="d-flex align-items-start gap-3">
            <div
              style={{
                flex: "0 0 auto",
                width: "120px",
                height: "120px",
                borderRadius: "8px",
                overflow: "hidden",
                border: "1px solid #dee2e6",
              }}
            >
              <img
                src={finalImageSrc} // Utilise directement l'URL propre (le 'value' prop)
                alt="Preview"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
            </div>
            <div style={{ flex: "1 1 auto", display: "flex", flexDirection: "column" }}>
              <div>
                <div className="d-flex align-items-center gap-2 mb-2">
                  <ImageOutlinedIcon style={{ color: "#6c757d" }} />
                  <strong style={{ fontSize: "0.9rem" }}>
                    {imageInfo?.name || "Image téléchargée"}
                  </strong>
                </div>
                {imageInfo && (
                  <div style={{ fontSize: "0.875rem", color: "#6c757d" }}>
                    <div>Taille: {imageInfo.size} MB</div>
                    <div>
                      Dimensions: {imageInfo.width} x {imageInfo.height} px
                    </div>
                  </div>
                )}
              </div>
              <Button
                variant="outline-danger"
                size="sm"
                onClick={handleClear}
                style={{
                  alignSelf: "flex-start",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.25rem",
                }}
              >
                <DeleteOutlineIcon fontSize="small" />
                Supprimer
              </Button>
            </div>
          </div>
        </div>
      )}

      {error && (
        <Alert variant="danger" className="mt-2 mb-0">
          {error}
        </Alert>
      )}
    </Form.Group>
  );
};

export default ImageUpload;